import {
  all, call, put, select, takeEvery,
} from 'redux-saga/effects';
import { selectEnterprise } from 'base/core/selectors';
import { IExpertItem } from 'pages/profile/interface';
import { CoreSliceActions } from 'base/core/slice';
import { selectAuthUser } from 'base/auth/selectors';
import { currencyFormat } from 'utils/formats';
import { IRestApiResponse } from '../../interfaces';
import { request } from '../../utils/request';
import API from './constants';
import { groupsAction } from './groups-list/slice';
import {
  FetchSummaryPayload,
  GetExpert,
  GetGroup,
  IDeleteItemStateAction,
  IEditExpertsStateAction,
  IGroupUsersFilterAction,
  UpdateGroupItem,
} from './interface';
import { groupSummaryActions } from './group-summary/slice';
import { groupUpdateActions } from './edit-group/slice';
import { selectExperts, selectGroup } from './edit-group/selector';

export function* getGroupListGenerator({ payload }: GetGroup): any {
  try {
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.GET_GROUPS.path}?keyword=${payload.keyword}&page=${payload.page}&limit=${payload.limit}&sortBy=${payload.sortBy}`,
        method: API.GET_GROUPS.method,
      },
      {},
      true,
      false,
    );
    if (response.statusCode === 200) {
      yield put(groupsAction.getGroupsSucceeded(response.data));
    } else {
      yield put(groupsAction.getGroupsFailed());
    }
  } catch (error) {
    yield put(groupsAction.getGroupsFailed());
  }
}

export function* getExpertsByEntGenerator({ payload }: GetExpert): any {
  try {
    const tenant = yield select(selectEnterprise);
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.GET_FETCH_EXPERTS.path}/${tenant.id}?keyword=${payload.keyword}&page=${payload.page}&limit=${payload.limit}&sortBy=${payload.sortBy}&status=ACTIVE&withDeleted=false`,
        method: API.GET_FETCH_EXPERTS.method,
      },
      null,
      true,
    );
    if (response.statusCode === 200) {
      yield put(groupUpdateActions.getGroupExpertsSucceeded(response.data));
    } else {
      yield put(groupUpdateActions.getGroupExpertsFailed());
    }
  } catch (error) {
    yield put(groupUpdateActions.getGroupExpertsFailed());
  }
}

export function* updateListItem({ payload }: UpdateGroupItem): any {
  try {
    const response: IRestApiResponse = yield call(
      request,
      { path: `${API.UPDATE_GROUPS.path}`, method: API.UPDATE_GROUPS.method },
      { ...payload },
      true,
      false,
    );
    if (response.statusCode === 201) {
      yield put(groupsAction.hideGroupItemSucceeded(response.data));
    } else {
      yield put(groupsAction.updateGroupItemFailed());
    }
  } catch (error) {
    yield put(groupsAction.updateGroupItemFailed());
  }
}

export function* editGroupGenerator({ payload }: IEditExpertsStateAction): any {
  try {
    const { id, data } = payload;
    const { articles, sessions, content } = data;

    const experts = (yield select(selectExperts)).map((expert: IExpertItem) => ({
      expert: expert.id,
      articles: articles
        .filter((article: any) => article.author.id === expert.id)
        .map((article: any) => ({ article: article._id })),
      sessions: sessions
        .filter((session: any) => session.expertId === expert.id)
        .map((session: any) => ({ session: session._id })),
      contents: content
        .filter((contentItem: any) => contentItem.expertId === expert.id)
        .map((contentItem: any) => ({
          content: contentItem._id,
        })),
    }));

    const groupData = { ...data };
    delete groupData.articles;
    delete groupData.sessions;
    delete groupData.content;
    delete groupData.experts;

    groupData.subscriptionFee = Number(data.subscriptionFee).toFixed(2).toLocaleString();

    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.UPDATE_GROUPS.path}`,
        method: API.UPDATE_GROUPS.method,
      },
      { id, ...groupData, experts },
      true,
    );

    if (response.statusCode === 201) {
      yield put(groupUpdateActions.updateGroupSucceed());
      yield put(
        CoreSliceActions.displayAlert({
          type: 'success',
          message: 'Group has been updated successfully.',
          btnText: 'Back to group list',
        }),
      );
    } else {
      yield put(groupUpdateActions.updateGroupFailed());
      yield put(
        CoreSliceActions.displayAlert({
          type: 'error',
          message: 'There was an error while updating the group.',
          btnText: 'Try updating again',
        }),
      );
    }
  } catch (error) {
    yield put(groupUpdateActions.updateGroupFailed());
    yield put(
      CoreSliceActions.displayAlert({
        type: 'error',
        message: 'There was an error while updating the group.',
        btnText: 'Try updating again',
      }),
    );
  }
}

export function* getGroupSummaryGenerator({ payload }: FetchSummaryPayload): any {
  try {
    const authUser = yield select(selectAuthUser);
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.GET_GROUP_SUMMARY.path}/${payload.id}`,
        method: API.GET_GROUP_SUMMARY.method,
      },
      {},
      true,
      false,
    );
    if (response.statusCode === 200) {
      const preferredCurrency = authUser?.attributes?.currency ?? 'AUD';
      const subscriberFee = yield call(
        currencyFormat,
        response.data.subscriptionFee,
        response.data.currency ?? 'AUD',
        preferredCurrency,
      );
      const groupSummary = {
        ...response.data,
        subscriptionFee: subscriberFee,
        currency: authUser?.attributes?.currency,
      };
      yield put(groupSummaryActions.fetchGroupSummarySucceed(groupSummary));
    } else {
      yield put(groupSummaryActions.fetchGroupSummaryFailed());
    }
  } catch (error) {
    yield put(groupSummaryActions.fetchGroupSummaryFailed());
  }
}

export function* getGroupDetailsGenerator({ payload }: FetchSummaryPayload): any {
  try {
    const authUser = yield select(selectAuthUser);
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.GET_GROUP_SUMMARY.path}/${payload.id}`,
        method: API.GET_GROUP_SUMMARY.method,
      },
      {},
      true,
      false,
    );
    if (response.statusCode === 200) {
      const preferredCurrency = authUser?.attributes?.currency ?? 'AUD';
      const subscriberFee = yield call(
        currencyFormat,
        response.data.subscriptionFee,
        response.data.currency ?? 'AUD',
        preferredCurrency,
      );
      const groupDetails = {
        ...response.data,
        subscriptionFee: subscriberFee,
        currency: authUser?.attributes?.currency,
      };
      // check if the current user is the group admin of this group
      if (window.location.pathname.includes(`/groups/edit/${groupDetails.id}`)) {
        if (groupDetails.groupAdmin !== '-' && groupDetails.groupAdmin !== authUser.id) {
          window.location.href = `/groups/summary/${groupDetails.id}`;
        }
      }
      yield put(groupUpdateActions.fetchGroupDetailsSucceeded(groupDetails));
    } else {
      yield put(groupUpdateActions.fetchGroupDetailsFailed());
    }
  } catch (error) {
    yield put(groupUpdateActions.fetchGroupDetailsFailed());
  }
}

export function* searchGroupUsersGenerator({ payload }: IGroupUsersFilterAction): any {
  try {
    const { groupId } = payload;
    const response: IRestApiResponse = yield call(
      request,
      {
        path: `${API.GET_GROUP_USERS.path.replace(':id', groupId)}?keyword=${payload.keyword}&page=${payload.page}&limit=${payload.limit}`,
        method: API.GET_GROUP_USERS.method,
      },
      null,
      true,
    );
    if (response.statusCode === 200) {
      yield put(
        groupUpdateActions.getUsersSucceeded({
          users: response.data.result,
          total: response.data.total,
        }),
      );
    } else {
      yield put(groupUpdateActions.getUsersFailed());
    }
  } catch (error) {
    yield put(groupUpdateActions.getUsersFailed());
  }
}

export function* deleteUsersGenerator({ payload }: IDeleteItemStateAction): any {
  try {
    const { id } = yield select(selectGroup);
    const response: IRestApiResponse = yield call(
      request,
      {
        path: API.DELETE_GROUP_USERS.path.replace(':id', id),
        method: API.DELETE_GROUP_USERS.method,
      },
      { emails: [payload] },
      true,
    );
    if (response.statusCode === 201) {
      yield put(groupUpdateActions.getUsers({
        page: 0,
        limit: 5,
        keyword: '',
        groupId: id,
      }));
      yield put(groupUpdateActions.deleteUserSucceeded(payload));
    }
    if (response.statusCode === 207) {
      const errors = response.data?.errors ?? [];
      yield all(
        errors.map((error: { message?: string }) => put(
          CoreSliceActions.setMessage({
            message: error?.message ?? 'Something went wrong',
            success: false,
          }),
        )),
      );
      yield put(groupUpdateActions.deleteUserFailed());
    } else {
      yield put(groupUpdateActions.deleteUserFailed());
    }
  } catch (error) {
    yield put(groupUpdateActions.deleteUserFailed());
  }
}

export function* groupsSaga() {
  yield all([
    takeEvery(groupsAction.getGroups.type, getGroupListGenerator),
    takeEvery(groupsAction.hideGroupItem.type, updateListItem),
    takeEvery(groupSummaryActions.fetchGroupSummary.type, getGroupSummaryGenerator),
    takeEvery(groupUpdateActions.fetchGroupDetails.type, getGroupDetailsGenerator),
    takeEvery(groupUpdateActions.getGroupExperts.type, getExpertsByEntGenerator),
    takeEvery(groupUpdateActions.getUsers.type, searchGroupUsersGenerator),
    takeEvery(groupUpdateActions.updateGroup.type, editGroupGenerator),
    takeEvery(groupUpdateActions.deleteUser.type, deleteUsersGenerator),
  ]);
}

export default groupsSaga;
